/**
 * gridFit — the SHAPE an existing marker should take from the module grid
 * (Indications panel "Fit to grid"). Pure: no store access, no writes.
 *
 *  - fault marks: the cell under the marker's centre (size + bearing);
 *  - String OC: the FULL footprint of the string the marker belongs to —
 *    the string the area derive names (max-area overlap, the same answer
 *    the "grid says" chip shows), falling back to the string under the
 *    centre when the derive has no grid answer.
 *
 * POSITION IS NEVER TOUCHED (owner ruling 2026-09-14): ortho and grid are
 * never perfectly aligned; the marker fits the ortho, the grid only names
 * the string/module. A marker sitting visibly off its cell stays the signal
 * that the grid needs correcting.
 */

import type { Indication } from '../stores/projectStore';
import type { SiteLayout } from '../domain/layout/types';
import { planTopologyUpdate } from '../domain/topology/applyDerivedTopology';
import {
  cellFitAtPoint,
  stringFitAtPoint,
  stringFitForEntity,
} from '../domain/topology/moduleGrid';

export interface MarkerGridFit {
  /** rect width (along the bearing) and height (across it), meters */
  w: number;
  h: number;
  directionDeg: number;
  kind: 'cell' | 'string';
  /** String OC only: the label of the string the shape was taken from */
  stringLabel?: string;
}

export function markerGridFit(
  indication: Pick<Indication, 'type' | 'geometry' | 'properties'>,
  layouts: SiteLayout[],
  center: [number, number],
): MarkerGridFit | null {
  if (indication.type === 'String OC') {
    const plan = planTopologyUpdate(indication, layouts);
    const derived = plan?.result.kind === 'grid' ? plan.result : null;
    const fit = (derived && stringFitForEntity(layouts, derived.source.layoutId, derived.cell.entityId))
      ?? stringFitAtPoint(layouts, center[0], center[1]);
    if (!fit) return null;
    return { w: fit.w, h: fit.h, directionDeg: fit.directionDeg, kind: 'string', stringLabel: fit.stringLabel };
  }
  const fit = cellFitAtPoint(layouts, center[0], center[1]);
  if (!fit) return null;
  return { w: fit.w, h: fit.h, directionDeg: fit.directionDeg, kind: 'cell' };
}
